// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './DocumentScanner.scss';
import { useTranslation } from 'react-i18next';

import { Modal, Button } from 'react-bootstrap';
import { format } from 'date-fns';
import { setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref } from 'firebase/storage';

import { fetchImageUrl, loadImageData, uploadImage } from '../../shared_components/utils/ImgUtils';
import DeliveryScanner from '../DeliveryScanner/DeliveryScanner';
import ImageSquare from '../../shared_components/ImageSquare/ImageSquare';
import DocumentInfo from './DocumentInfo';
import { firestore, storage } from '../../context/firebase';

// Importing icons
import back_icon from '../../assets/icons/white/back_small.png';
import magic_wand_icon from '../../assets/icons/white/magic_wand.png';
import {
  DELIVERIES_SETTINGS_GLOBAL_PATH,
  DELIVERY_SETTINGS_DOCUMENT_PATH,
  SCANNED_DOCUMENT_DOCUMENT_PATH,
  SCANNED_DOCUMENT_STORAGE_PATH,
} from '../../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../../stores/authStore';
import { useGetDocument } from '../../shared_components/hooks/useGetDocument';

export default function DocumentScanner({ deliveryId, documentsData, show, onHide, handleDocumentsDataFieldChange }) {
  const { t } = useTranslation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const logEventCustom = useAuthStore((state) => state.logEventCustom);
  const [documentImage, setDocumentImage] = React.useState(null);
  const [showCamera, setShowCamera] = React.useState(false);
  const [showFoto, setShowFoto] = React.useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = React.useState();

  const { data: globalPromptData } = useGetDocument(doc(firestore, DELIVERIES_SETTINGS_GLOBAL_PATH));
  const { data: clientPromptData } = useGetDocument(
    doc(firestore, DELIVERY_SETTINGS_DOCUMENT_PATH(currentUserClientId)),
  );

  /* Load the global and client prompt for document scanning */
  React.useEffect(() => {
    const documentIds = Object.keys(documentsData?.scanned_documents || {});
    if (documentIds.length > 0) {
      setSelectedDocumentId(documentIds[0]);
    }
  }, []);

  React.useEffect(() => {
    async function loadFullResImage() {
      const url = await fetchImageUrl(
        storage,
        documentsData?.scanned_documents?.[selectedDocumentId]?.image_storage_path,
      );
      const img = await loadImageData(url);
      setDocumentImage(img.src);
    }

    if (selectedDocumentId && documentsData?.scanned_documents?.[selectedDocumentId]?.image_storage_path) {
      loadFullResImage();
    }
  }, [documentsData?.scanned_documents?.[selectedDocumentId]?.image_storage_path]);

  async function addDocumentImage(image) {
    // Upload the image to storage and retrieve its URL
    const imagePath = SCANNED_DOCUMENT_STORAGE_PATH(currentUserClientId, format(new Date(), 'yyyy-MM-dd_HH-mm-ss'));
    const image_URL = await uploadImage(storage, image, imagePath);
    const storageRef = ref(storage, imagePath);

    // Create a new document for the document and its Gemini prompt
    const scannedDocId = `${currentUserClientId}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}`;
    setDoc(doc(firestore, SCANNED_DOCUMENT_DOCUMENT_PATH(currentUserClientId, deliveryId, scannedDocId)), {
      image_url: image_URL,
      image_storage_path: imagePath,
      image_storage_path_full: `gs://${storageRef.bucket}/${storageRef.fullPath}`,
      created_at: serverTimestamp(),
      processing_status: 'pending',
      delivery_id: deliveryId,
      prompt_global: globalPromptData?.scan_extraction_prompt ?? ' ',
      prompt_client: clientPromptData?.scan_extraction_prompt ?? ' ',
    });

    // Add the image url to the document data
    handleDocumentsDataFieldChange({
      scanned_documents: {
        [scannedDocId]: {
          scanned_document_id: scannedDocId,
          image_url: image_URL,
          image_storage_path: imagePath,
          created_at: serverTimestamp(),
          processing_status: 'pending',
        },
      },
    });

    // Log event
    logEventCustom('add_scanned_document', { delivery_id: deliveryId, scanned_document_id: scannedDocId });

    setSelectedDocumentId(scannedDocId);
  }

  function getScanner() {
    return (
      <div className='document-scanner-container'>
        <DeliveryScanner
          addDeliveryImage={addDocumentImage}
          viewLastDeliveryImage={() => setShowCamera(false)}
          viewOverview={() => setShowCamera(false)}
          showImageAfterCapture={true}
        />
        <button className='back-button' onClick={() => setShowCamera(false)}>
          <img src={back_icon} />
        </button>
      </div>
    );
  }

  function getImageView() {
    return (
      <div className='document-image-view'>
        <img className='document-image' src={documentImage} />
        <button className='back-button' onClick={() => setShowFoto(false)}>
          <img src={back_icon} />
        </button>
      </div>
    );
  }

  function editDocumentField(e) {
    handleDocumentsDataFieldChange({
      scanned_documents: {
        [selectedDocumentId]: {
          [e.target.name]: e.target.value,
        },
      },
      complete_data: {
        [e.target.name]: e.target.value,
      },
    });
    logEventCustom('edit_weighing_slip', { delivery_id: deliveryId, field: e.target.name, value: e.target.value });
  }

  function getScannedDocument() {
    return (
      <div className='document-scanner'>
        <div className='document-scanner-documents'>
          <div className='document-scanner-documents-card-wrapper'>
            <div className='document-scanner-documents-card-new' onClick={() => setShowCamera(true)}>
              <p>+</p>
            </div>
            {Object.entries(documentsData?.scanned_documents || {})
              .sort()
              .map(([scannedDocumentId, scannedDocument]) => {
                const isSelected = selectedDocumentId === scannedDocumentId;
                return (
                  <div
                    className={`document-square-wrapper ${isSelected && 'selected'}`}
                    onClick={() => {
                      if (selectedDocumentId === scannedDocumentId) {
                        setShowFoto(true);
                      } else {
                        setSelectedDocumentId(scannedDocumentId);
                      }
                    }}
                    key={scannedDocumentId}
                  >
                    <ImageSquare
                      key={scannedDocumentId}
                      storage={storage}
                      imageStoragePath={scannedDocument?.image_storage_path}
                      icons={[]}
                    />
                  </div>
                );
              })}
            {!documentsData?.scanned_documents && (
              <div className='empty-documents-info'>
                <p className='empty-documents-info-text'>{t('scanned_document_extraction_info')}</p>
                <img className='magic-wand-icon' src={magic_wand_icon} />
              </div>
            )}
          </div>
        </div>
        {selectedDocumentId && (
          <DocumentInfo
            documentsData={documentsData?.scanned_documents?.[selectedDocumentId]}
            editDocumentField={editDocumentField}
          />
        )}
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide} backdrop='static' className='modal'>
      <div
        className={`modal-box document-scanner-modal-box ${showCamera ? 'scanner' : showFoto ? 'foto-view' : 'overview'}`}
      >
        {showCamera ? (
          getScanner()
        ) : showFoto ? (
          getImageView()
        ) : (
          <>
            {getScannedDocument()}
            <Modal.Footer>
              <Button className='modal-button' variant='secondary' onClick={onHide}>
                {t('general_close')}
              </Button>
            </Modal.Footer>
          </>
        )}
      </div>
    </Modal>
  );
}
